import { useMemo } from 'react';
import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { rgba } from 'polished';
import styled, { css, useTheme } from 'styled-components';

import { GradientHeader } from '@/components/design-system';
import { ToutWrapper } from '@/components/design-system/ToutWrapper';
import Img from '@/components/global/Img';
import { GRID_SIZE } from '@/components/global/PaperGrid';
import { type ContentfulAssetType } from '@/data/graphql-fragments/ContentfulAsset';
import useMediaQuery from '@/utils/useMediaQuery';

const StyledToutWrapper = styled(ToutWrapper)`
  padding: 19px 0 19px;
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  padding: 0 ${GRID_SIZE}px;
  margin: 0 auto;
  z-index: 1;
  position: relative;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  text-align: center;
  width: calc(100% / 6 * 2 - 1px);
  height: calc(100% / 5 * 3 - 1px);
  left: calc(100% / 6 * 2);
  top: calc(100% / 5);
  padding: 0 30px;
  z-index: 1;
`;

const InlineTitle = styled.div`
  padding: 25px;
  border-left: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  border-right: 1px solid ${({ theme }) => theme.borderColor.MUTED};
`;

const Logos = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  border-top: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  border-left: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  position: relative;
`;

const InlineLogos = styled(Logos)<{ $count: number }>`
  grid-template-columns: ${({ $count }) =>
    $count === 5 ? '1fr 1fr 1fr 1fr 1fr' : '1fr 1fr 1fr'};
`;

const Customer = styled.div<{
  $hideBtm?: boolean;
  $hideRight?: boolean;
}>`
  display: flex;
  align-items: center;
  position: relative;
  justify-content: center;
  height: 80px;
  flex: none;
  min-width: 0;
  border-right: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  border-bottom: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  transition: background-color ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    box-shadow: 0 0 0 1px ${({ theme }) => theme.borderColor.MUTED};
    transition: opacity ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};
  }

  &:hover {
    background-color: ${({ theme }) => rgba(theme.highlightColor, 0.05)};

    &::after {
      opacity: 1;
    }
  }

  ${({ $hideBtm }) =>
    $hideBtm &&
    css`
      border-bottom: none;
    `}

  ${({ $hideRight }) =>
    $hideRight &&
    css`
      border-right: none;
    `}
`;

const Logo = styled.div`
  display: flex;

  img {
    ${({ theme }) => theme.customerLogoColor};
    height: 35px;
  }
`;

interface CustomerLogo {
  logo: ContentfulAssetType;
  name: string;
}

interface CustomerBannerQuery {
  allContentfulCustomer: {
    nodes: CustomerLogo[];
  };
}

const LOGOS_TO_HIDE = [
  { loc: 8, btm: true, right: true },
  { loc: 9, btm: true },
  { loc: 14, btm: true, right: true },
  { loc: 15, btm: true },
  { loc: 20, right: true },
  { loc: 21 }
];

const DESIRED_LOGO_SORT = [
  'Reddit',
  'Notion',
  'Brex',
  'Anthropic',
  'Hinge',
  'SeatGeek',
  'Babylist',
  'ClickUp',
  null,
  null,
  'Algolia',
  'Fivetran',
  'Loom',
  'AllTrails',
  null,
  null,
  'Modern Treasury',
  'Hightouch',
  'Ramp',
  'Recursion',
  null,
  null,
  'Sedgwick',
  'AppFolio',
  'Avalara',
  'Chesapeake Energy',
  'Safety Culture',
  'On Running',
  'Doximity',
  'Pure Storage'
];

export const CustomerBanner: React.ComponentType = React.memo(() => {
  const theme = useTheme();
  const collapseToTen = useMediaQuery(theme.mediaQueryString.LARGE);
  const collapseToSix = useMediaQuery(theme.mediaQueryString.MEDIUM);

  const data: CustomerBannerQuery = useStaticQuery(graphql`
    query {
      allContentfulCustomer {
        nodes {
          name
          logo {
            ...ContentfulAsset
          }
        }
      }
    }
  `);

  const logosToShow = [...data.allContentfulCustomer.nodes].filter(o =>
    DESIRED_LOGO_SORT.includes(o.name)
  );

  const logoArray = useMemo(
    () =>
      DESIRED_LOGO_SORT.map(item => {
        return logosToShow.find(o => o.name === item) as CustomerLogo;
      }),
    [logosToShow]
  );

  const title = (
    <GradientHeader
      css={`
        padding-top: 4px !important;
      `}
      renderAs="h2">
      Trusted by leading data teams
    </GradientHeader>
  );

  if (collapseToTen || collapseToSix) {
    const logoRowCount = collapseToTen && !collapseToSix ? 5 : 3;
    const cleanArray = [...logoArray].filter(n => n !== undefined);
    const topLogos = cleanArray.slice(0, logoRowCount);
    const bottomLogos = cleanArray.slice(logoRowCount, logoRowCount * 2);

    return (
      <StyledToutWrapper>
        <Inner>
          <InlineLogos $count={logoRowCount}>
            {topLogos.map((item, index) => (
              <Customer key={index}>
                <Logo>
                  <Img alt={`Hex customer: ${item.name}`} asset={item.logo} />
                </Logo>
              </Customer>
            ))}
          </InlineLogos>
          <InlineTitle>{title}</InlineTitle>
          <InlineLogos $count={logoRowCount}>
            {bottomLogos.map((item, index) => (
              <Customer key={index}>
                <Logo>
                  <Img
                    alt={`Hex customer: ${item.name}`}
                    asset={item.logo}
                    loading="lazy"
                  />
                </Logo>
              </Customer>
            ))}
          </InlineLogos>
        </Inner>
      </StyledToutWrapper>
    );
  }

  return (
    <StyledToutWrapper>
      <Inner>
        <Logos>
          <Title>{title}</Title>
          {logoArray.map((item, index) => {
            const shouldHide = LOGOS_TO_HIDE.find(o => o.loc === index);
            return (
              <Customer
                key={index}
                $hideBtm={shouldHide?.btm}
                $hideRight={shouldHide?.right}>
                {item && (
                  <Logo>
                    <Img alt={`Hex customer: ${item.name}`} asset={item.logo} />
                  </Logo>
                )}
              </Customer>
            );
          })}
        </Logos>
      </Inner>
    </StyledToutWrapper>
  );
});
